import { useEffect, useState } from 'react';
import { readPrerenderData, publishPrerenderData } from '../utils/prerenderData';

/**
 * The rotating agent name in the hero, and the seeding that lets it hydrate.
 *
 * WHY THIS IS A HOOK AND NOT TWO COPIES
 *
 * This existed twice, identically: src/pages/Home.js (which /about renders) and
 * src/pages/newHomeforDemo.js (which / renders). The same eighteen-item list,
 * the same 500ms interval, the same bug.
 *
 * And the bug was found twice, a build apart. Fixing Home.js left / still
 * failing, and the second copy was only found by removing the App-level
 * Suspense a second time and reading the component React named. Two files
 * holding the same code is how one gets a fix and the other does not; it had
 * already happened today with the SITE constant and the date formatter.
 *
 * THE BUG, since the seeding below looks arbitrary without it
 *
 * The name advances every 500ms. The prerenderer captures seconds after load,
 * so the saved file holds whatever name was showing, while every client's first
 * render shows the first one. A different word in the same span on every single
 * visit, and React answers by discarding the page:
 *
 *     Hydration failed ... at section, at NewHome
 *
 * The FRAME is seeded rather than the animation frozen, so the rotation carries
 * on from where the capture left it and a visitor sees no difference.
 *
 * Published from an EFFECT keyed on the index, never from inside the updater:
 * that reached the saved file not at all the first time it was tried. See the
 * note at the top of src/utils/prerenderData.js.
 */

// One list, so the two heroes cannot drift apart. They were identical when this
// was extracted; that was luck rather than discipline.
export const HERO_AGENTS = [
  { name: 'Interview', color: '#FF5733' },
  { name: 'Speech Therapy', color: '#33FF57' },
  { name: 'Language Learning', color: '#3357FF' },
  { name: 'Spoken English', color: '#FF33A1' },
  { name: 'Life Coach', color: '#FFD733' },
  { name: 'Career Advisor', color: '#FF5733' },
  { name: 'Tutor', color: '#33FFD5' },
  { name: 'Tax Filer', color: '#A1FF33' },
  { name: 'Personal Assistant', color: '#FF33F6' },
  { name: 'Web Form Filler', color: '#FF5733' },
  { name: 'Revise', color: '#3357FF' },
  { name: 'Sales', color: '#33A1FF' },
  { name: 'Marketing', color: '#FFD733' },
  { name: 'Schedule Task', color: '#FF33A1' },
  { name: 'Calendar', color: '#57FF33' },
  { name: 'File Tickets', color: '#3357FF' },
  { name: 'Mathematician', color: '#FF5733' },
  { name: 'Famous Scientist', color: '#33FFD5' },
];

const ROTATE_MS = 500;

/**
 * @returns {{ agents: Array, index: number, current: object }}
 */
export function useRotatingHeroAgent(agents = HERO_AGENTS) {
  const [index, setIndex] = useState(() => readPrerenderData('heroAgentIndex') ?? 0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % agents.length);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [agents]);

  useEffect(() => {
    publishPrerenderData('heroAgentIndex', index);
  }, [index]);

  return { agents, index, current: agents[index] };
}

export default useRotatingHeroAgent;
